import { User } from 'states/UsersState';
import { GeoLevelName, geoLevelFromRoleCode } from './geoScope';

/** The geographic filter value for users whose role covers no particular area (admins). */
export const NO_GEO = 'none';

/** From wide to narrow, the order the filter lists them in. */
export const GEO_LEVELS: GeoLevelName[] = ['state', 'district', 'block', 'facility', 'institute'];

/** Each filter holds the values ticked; none ticked means no restriction. */
export interface UserFilterValue {
  /** Role names. */
  roles: string[];
  /** GeoLevelNames, and NO_GEO. */
  geos: string[];
}

export const NO_FILTERS: UserFilterValue = { roles: [], geos: [] };

export const hasFilters = (f: UserFilterValue): boolean => f.roles.length > 0 || f.geos.length > 0;

export const userRoleName = (u: Pick<User, 'roles'>): string => u.roles?.[0]?.name || '';

/** The area level a user is scoped to, or NO_GEO when their role covers no particular area. */
export const userGeoLevel = (u: Pick<User, 'roles'>): string => geoLevelFromRoleCode(u.roles?.[0]?.code) ?? NO_GEO;

export const matchesUserFilters = (u: Pick<User, 'roles'>, f: UserFilterValue): boolean =>
  (f.roles.length === 0 || f.roles.includes(userRoleName(u))) && (f.geos.length === 0 || f.geos.includes(userGeoLevel(u)));

export interface FilterOption {
  value: string;
  count: number;
}

/** The roles that appear in this list, A to Z, with how many users hold each. */
export const roleOptions = (users: Pick<User, 'roles'>[]): FilterOption[] => {
  const counts = new Map<string, number>();
  users.forEach((u) => {
    const name = userRoleName(u);
    if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
  });
  return Array.from(counts, ([value, count]) => ({ value, count })).sort((a, b) => a.value.localeCompare(b.value));
};

/** The area levels that appear in this list, wide to narrow, with "no geographic scope" last. */
export const geoOptions = (users: Pick<User, 'roles'>[]): FilterOption[] => {
  const counts = new Map<string, number>();
  users.forEach((u) => counts.set(userGeoLevel(u), (counts.get(userGeoLevel(u)) ?? 0) + 1));
  return [...GEO_LEVELS, NO_GEO].filter((v) => counts.has(v)).map((value) => ({ value, count: counts.get(value) ?? 0 }));
};
